import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HeartIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import qumashiLogo from '../../../assets/qumashi-logo.webp';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { UserMenu } from '../UserMenu';
import { useAccountSheet } from '../../../contexts/AccountSheetContext';
import { selectCartItemCount } from '../../../store/cartSlice';
import { selectWishlistCount } from '../../../store/wishlistSlice';
import { ACCOUNT_WISHLIST_PATH } from '../../../config/accountPaths';
import { useNavPersona } from '../../../contexts/NavPersonaContext';
import { useNavbarBadges } from '../../../hooks/useNavbarBadges';
import {
  buildUserNavMenuConfig,
} from '../../../config/userNavMenu';
import { getUserInitials } from '../../../utils/userHelpers';
import { getRoutePrefetchHandlers } from '../../../utils/routePrefetch';
import { IUser } from '../../../types/auth.types';
import { NavbarMobileMenu } from './NavbarMobileMenu';
import { requestCartHydration } from '../../../hooks/useEnsureCartLoaded';
import { useNavbarScroll } from './useNavbarScroll';
import './Navbar.css';

interface NavbarProps {
  isCartPage: boolean;
  designPreview: boolean;
}

/* Isolated islands — cart/wishlist updates must not re-render the whole bar. */
const NavbarCartLink = memo(function NavbarCartLink() {
  const dispatch = useAppDispatch();
  const count = useAppSelector(selectCartItemCount);
  const handleCartIntent = useCallback(() => {
    requestCartHydration(dispatch);
  }, [dispatch]);
  return (
    <Link
      to="/cart"
      className="navbar__icon-btn"
      aria-label={count > 0 ? `Cart, ${count} items` : 'Cart'}
      onPointerDown={handleCartIntent}
      onFocus={handleCartIntent}
      {...getRoutePrefetchHandlers('/cart')}
    >
      <ShoppingBagIcon className="navbar__icon" />
      {count > 0 ? (
        <span className="navbar__badge navbar__badge--cart">
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
});

const NavbarWishlistLink = memo(function NavbarWishlistLink() {
  const count = useAppSelector(selectWishlistCount);
  return (
    <Link
      to={ACCOUNT_WISHLIST_PATH}
      className="navbar__icon-btn navbar__icon-btn--hidden-mobile"
      aria-label={
        count > 0 ? `Wishlist, ${count} saved` : 'Wishlist'
      }
      {...getRoutePrefetchHandlers(ACCOUNT_WISHLIST_PATH)}
    >
      <HeartIcon className="navbar__icon" />
      {count > 0 ? (
        <span className="navbar__badge navbar__badge--wishlist">
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
});

const AVATAR_GRADIENT: Record<string, string> = {
  buyer: 'linear-gradient(to bottom right, #3b3b98, #9b59b6)',
  creator: 'linear-gradient(to bottom right, #c026d3, #db2777, #f43f5e)',
  seller: 'linear-gradient(to bottom right, #553c9a, #7c3aed, #9333ea)',
  owner: 'linear-gradient(to bottom right, #4f46e5, #7c3aed)',
  staff: 'linear-gradient(to bottom right, #334155, #4338ca)',
};

const PERSONA_DOT: Record<string, string> = {
  buyer: '#a855f7',
  creator: '#d946ef',
  seller: '#8b5cf6',
  owner: '#6366f1',
  staff: '#475569',
};

const NavbarMobileAvatar = memo(function NavbarMobileAvatar({
  user,
  onClick,
}: {
  user: IUser;
  onClick: () => void;
}) {
  const { pathname } = useLocation();
  const menu = buildUserNavMenuConfig(user, pathname);
  const { menuPersona } = useNavPersona();
  const { count: badgeCount } = useNavbarBadges(menuPersona, true);

  return (
    <button
      type="button"
      data-testid="navbar-account-menu-trigger-mobile"
      className="navbar__avatar-btn navbar__mobile-only"
      onClick={onClick}
      aria-label={
        badgeCount > 0
          ? `Open ${menu.personaLabel} menu, ${badgeCount} notifications`
          : `Open ${menu.personaLabel} menu`
      }
    >
      <span
        className="navbar__avatar"
        style={{ background: AVATAR_GRADIENT[menu.accent] ?? AVATAR_GRADIENT.buyer }}
      >
        {getUserInitials(user)}
      </span>
      {badgeCount > 0 ? (
        <span className="navbar__avatar-badge" aria-hidden>
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      ) : (
        <span
          className="navbar__avatar-dot"
          style={{
            backgroundColor: PERSONA_DOT[menu.accent] ?? PERSONA_DOT.buyer,
          }}
          aria-hidden
        />
      )}
    </button>
  );
});

const NavbarDesktopLinks = memo(function NavbarDesktopLinks({
  pathname,
}: {
  pathname: string;
}) {
  const { chrome } = useNavPersona();
  if (chrome.centerLinks.length === 0) {
    return <div className="navbar__links" aria-hidden />;
  }
  return (
    <div className="navbar__links">
      {chrome.centerLinks.map((link) => {
        const active = pathname.includes(link.path);
        return (
          <Link
            key={link.path}
            to={link.path}
            className={`navbar__link${active ? ' navbar__link--active' : ''}`}
            {...getRoutePrefetchHandlers(link.path)}
          >
            {link.name}
          </Link>
        );
      })}
    </div>
  );
});

const NavbarAuthActions = memo(function NavbarAuthActions({
  mobileOpen,
  onMenuToggle,
  onMenuOpenTouch,
  hideMobileAccount,
}: {
  mobileOpen: boolean;
  onMenuToggle: () => void;
  onMenuOpenTouch: () => void;
  hideMobileAccount?: boolean;
}) {
  const { open: openAccountSheet } = useAccountSheet();
  const { user } = useAppSelector((state) => state.auth);
  const { chrome } = useNavPersona();

  return (
    <div className="navbar__actions">
      {chrome.showWishlist ? <NavbarWishlistLink /> : null}
      {chrome.showCart ? <NavbarCartLink /> : null}

      {chrome.showAccount && user ? (
        <>
          <div className="navbar__desktop-account">
            <UserMenu user={user} />
          </div>
          {!hideMobileAccount ? (
            <NavbarMobileAvatar user={user} onClick={openAccountSheet} />
          ) : null}
        </>
      ) : null}

      {chrome.showGuestAuth ? (
        <div className="navbar__guest">
          <Link to="/login" className="navbar__guest-link">
            Sign In
          </Link>
          <Link to="/shop" className="navbar__guest-cta">
            Shop now
          </Link>
        </div>
      ) : null}

      <button
        type="button"
        className="navbar__menu-btn"
        data-open={mobileOpen ? 'true' : 'false'}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
        onPointerDown={onMenuOpenTouch}
        onClick={onMenuToggle}
      >
        <span className="navbar__menu-lines" aria-hidden>
          <span className="navbar__menu-line" />
          <span className="navbar__menu-line" />
          <span className="navbar__menu-line" />
        </span>
      </button>
    </div>
  );
});

function Navbar({
  isCartPage,
  designPreview,
}: NavbarProps) {
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const pathname = location.pathname;
  const isHomePage = pathname === '/';
  const isPdpPage = pathname.startsWith('/product/');
  const isShopPage = pathname.startsWith('/shop');

  const [mobileOpen, setMobileOpen] = useState(false);
  const menuOpenedAtRef = useRef(0);
  const suppressClickAfterTouchRef = useRef(false);

  useNavbarScroll(navRef, pathname);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const markMenuOpened = useCallback(() => {
    menuOpenedAtRef.current = Date.now();
  }, []);

  // Touch: open on finger-down (instant). Close stays on click to avoid ghost taps.
  const handleMenuOpenTouch = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (event.pointerType !== 'touch' || mobileOpen) return;
      markMenuOpened();
      suppressClickAfterTouchRef.current = true;
      setMobileOpen(true);
    },
    [mobileOpen, markMenuOpened],
  );

  const handleMenuToggle = useCallback(() => {
    if (suppressClickAfterTouchRef.current) {
      suppressClickAfterTouchRef.current = false;
      return;
    }
    setMobileOpen((open) => {
      if (!open) markMenuOpened();
      return !open;
    });
  }, [markMenuOpened]);

  const handleMenuClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  if (isCartPage || designPreview) return null;

  return (
    <>
      <nav
        ref={navRef}
        className={`navbar${isPdpPage ? ' hidden lg:block' : ''}`}
        data-hero={isHomePage ? '1' : '0'}
        data-shop={isShopPage ? '1' : '0'}
        data-scrolled="0"
      >
        <div className="navbar__container">
          <div className="navbar__row">
            <Link
              to="/"
              className="navbar__logo"
              {...getRoutePrefetchHandlers('/')}
            >
              <div className="navbar__logo-mark">
                <img
                  src={qumashiLogo}
                  alt="Qumashi Logo"
                  width={28}
                  height={28}
                  className="navbar__logo-img"
                  decoding="async"
                />
                <div>
                  <span className="navbar__logo-qumas">Qumas</span>
                  <span className="navbar__logo-hi">hi</span>
                  <div className="navbar__logo-underline" aria-hidden />
                </div>
              </div>
            </Link>

            <NavbarDesktopLinks pathname={pathname} />

            <NavbarAuthActions
              mobileOpen={mobileOpen}
              onMenuToggle={handleMenuToggle}
              onMenuOpenTouch={handleMenuOpenTouch}
              hideMobileAccount={isShopPage}
            />
          </div>
        </div>
      </nav>

      <NavbarMobileMenu
        isOpen={mobileOpen}
        onClose={handleMenuClose}
        openedAtRef={menuOpenedAtRef}
      />
    </>
  );
}

export default memo(Navbar);
